import React, { useState, useEffect, useCallback } from 'react'
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import Loader from '../../../components/common/Loader';
import NoData from '../../../components/common/NoData';
import { getClassroomDetails, getAllAssessmentToClassroom, addAssessmentToClassroom, removeAssessmentFromClassroom, updateAssessmentInClassroom, copyAssessment } from '../../../services/adminService';
import { decodeData } from '../../../utils/encodeAndEncode';
import { encodeData } from '../../../utils/encodeAndEncode';
import timeZones from '../../../utils/timeZone.json';
import { usePermissions } from '../../../context/PermissionsContext';
const DefaultTimeZone = process.env.REACT_APP_DEFAULT_TIMEZONE;

function ClassroomAssessment() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { classroomId: encodedClassroomId } = useParams();
  const classroomId = decodeData(encodedClassroomId);

  // State management
  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalRecords: 0
  });

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    assessmentName: '',
    points: '0',
    isTimeBased: false,
    status: 'Inactive',
    duration: '',
    passingPercentage: '35',
    opensAt: '',
    closesAt: '',
    visibility: 'visible',
    timeZone: DefaultTimeZone // Add timezone with default value
  });

  // Update editFormData state
  const [editFormData, setEditFormData] = useState({
    assessmentId: '',
    assessmentName: '',
    points: '0',
    isTimeBased: false,
    status: 'Inactive',
    duration: '',
    passingPercentage: '35',
    opensAt: '',
    closesAt: '',
    visibility: 'hidden',
    timeZone: DefaultTimeZone // Add timezone to edit form
  });

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  // Error states
  const [formErrors, setFormErrors] = useState({});

  // Delete and copy states
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [availableClassrooms, setAvailableClassrooms] = useState([]);
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);
  const [classroomSearchTerm, setClassroomSearchTerm] = useState('');

  // Add new states for timezone
  const [timeZoneSearch, setTimeZoneSearch] = useState('');
  const [isTimeZoneDropdownOpen, setIsTimeZoneDropdownOpen] = useState(false);

  // Fetch assessments from API
  const fetchAssessments = useCallback(async (page = 1, search = '') => {
    if (!classroomId) {
      toast.error("Classroom ID is missing");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        class_id: classroomId,
        page: page,
        limit: pagination.limit,
        search: search,
        startDate: "",
        endDate: ""
      };

      const response = await getAllAssessmentToClassroom(payload);
      // console.log('response------------------------------------------', response);

      if (response.success) {
        // Transform API data to match our component's expected format
        const transformedAssessments = response.assessments.map(assessment => ({
          id: assessment.id,
          title: assessment.assessment_name,
          status: assessment.is_active === 1 ? "Active" : "Inactive",
          is_active: assessment.is_active,
          is_visible: assessment.is_visible,
          duration: `${assessment.duration} mins`,
          passingPercentage: `${assessment.pass_percentage}%`,
          opensAt: assessment.activate_time,
          closesAt: assessment.deactivate_time,
          manualActivation: assessment.is_time_based === 0,
          isTimeBased: assessment.is_time_based === 1,
          stats: {
            passingPercentage: `${assessment.pass_percentage}%`,
            attended: assessment.completed_count || 0,
            noOfDue: assessment.total_questions || 0
          },
          // Store raw values for edit form
          activate_time: assessment.activate_time,
          deactivate_time: assessment.deactivate_time,
          pass_percentage: assessment.pass_percentage,
          earning_points: assessment.earning_points,
          raw_duration: assessment.duration,
          raw_time_zone: assessment.time_zone // Add raw timezone
        }));

        setAssessments(transformedAssessments);
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          totalPages: response.pagination.totalPages,
          totalRecords: response.pagination.totalRecords
        });
      } else {
        toast.error(response.message || "Failed to fetch assessments");
      }
    } catch (error) {
      console.error("Error fetching assessments:", error);
      toast.error("An error occurred while fetching assessments");
    } finally {
      setLoading(false);
    }
  }, [classroomId, pagination.limit]);

  // Fetch available classrooms for copy functionality
  const fetchAvailableClassrooms = useCallback(async () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("user"));
      const user_id = userDetails?.id;

      const payload = {
        page: 1,
        limit: 10000,
        search: "",
        user_id: user_id
      };

      const response = await getClassroomDetails(payload);

      if (response && response.data && response.data.classes) {
        const formattedClassrooms = response.data.classes
          .filter(cls => cls.id !== classroomId) // Exclude current classroom
          .map(cls => ({
            id: cls.id,
            name: cls.cls_name
          }));
        setAvailableClassrooms(formattedClassrooms);
      } else {
        toast.error("Failed to fetch classrooms for copying");
        setAvailableClassrooms([]);
      }
    } catch (error) {
      console.error("Error fetching classroom details:", error);
      toast.error("An error occurred while fetching classrooms.");
      setAvailableClassrooms([]);
    }
  }, [classroomId]);

  // Initialize data on component mount
  useEffect(() => {
    if (classroomId) {
      fetchAssessments(1, '');
      fetchAvailableClassrooms();
    }
  }, [classroomId, fetchAssessments, fetchAvailableClassrooms]);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchAssessments(1, value);
  };

  // Clear form errors when user types
  const clearFieldError = (field) => {
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Format date for datetime-local input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return '';
    }
  };

  // Format date to API format with timezone
  const formatToLocalString = (dateTimeString) => {
    if (!dateTimeString) return "";
    const localDate = new Date(dateTimeString);

    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    const seconds = String(localDate.getSeconds()).padStart(2, '0');

    const tzOffset = -localDate.getTimezoneOffset();
    const tzOffsetHours = Math.floor(Math.abs(tzOffset) / 60);
    const tzOffsetMinutes = Math.abs(tzOffset) % 60;
    const tzOffsetSign = tzOffset >= 0 ? '+' : '-';
    const tzOffsetString = `${tzOffsetSign}${String(tzOffsetHours).padStart(2, '0')}:${String(tzOffsetMinutes).padStart(2, '0')}`;

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${tzOffsetString}`;
  };

  function HandleAssessmentManage(assessmentId) {
    if (!permissions.classroom_assessment_manage) {
      toast.warning("You don't have permission to manage assessments");
      return;
    }
    const encodedClassroomId = encodeData(classroomId);
    const encodedAssessmentId = encodeData(assessmentId);
    navigate(`/admin/live-sessions/classroom-dashboard/${encodedClassroomId}/assessment/${encodedAssessmentId}/ClassroomAssessmentDetails`);
  }

  // Form input handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    clearFieldError(name);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    clearFieldError(name);
  };

  // Modal handlers
  const handleModalClose = () => {
    setShowModal(false);
    setFormErrors({});
    setFormData({
      assessmentName: '',
      points: '0',
      isTimeBased: false,
      status: 'Inactive',
      duration: '',
      passingPercentage: '35',
      opensAt: '',
      closesAt: '',
      visibility: 'visible',
      timeZone: DefaultTimeZone // Also update reset state to visible
    });
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setFormErrors({});
    setEditFormData({
      assessmentId: '',
      assessmentName: '',
      points: '0',
      isTimeBased: false,
      status: 'Inactive',
      duration: '',
      passingPercentage: '35',
      opensAt: '',
      closesAt: '',
      visibility: 'hidden',
      timeZone: DefaultTimeZone
    });
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setSelectedAssessment(null);
  };

  const handleCopyModalClose = () => {
    setShowCopyModal(false);
    setSelectedAssessment(null);
    setSelectedClassrooms([]);
    setClassroomSearchTerm('');
  };

  // Validation function
  const validateForm = (data) => {
    const errors = {};

    if (!data.assessmentName.trim()) {
      errors.assessmentName = "Assessment name is required";
    } else if (data.assessmentName.trim().length < 3) {
      errors.assessmentName = "Assessment name must be at least 3 characters";
    }

    if (!data.duration.trim()) {
      errors.duration = "Duration is required";
    } else if (isNaN(data.duration) || Number(data.duration) <= 0) {
      errors.duration = "Duration must be a positive number";
    }

    if (data.passingPercentage === undefined || data.passingPercentage === null ||
      (typeof data.passingPercentage === 'string' && data.passingPercentage.trim() === '')) {
      errors.passingPercentage = "Passing percentage is required";
    } else {
      const passPercentage = Number(data.passingPercentage);
      if (isNaN(passPercentage) || passPercentage < 1 || passPercentage > 100) {
        errors.passingPercentage = "Passing percentage must be between 1 and 100";
      }
    }

    if (data.isTimeBased) {
      if (!data.opensAt) {
        errors.opensAt = "Opens At is required";
      }

      if (!data.closesAt) {
        errors.closesAt = "Closes At is required";
      }

      if (data.opensAt && data.closesAt) {
        const opensDate = new Date(data.opensAt);
        const closesDate = new Date(data.closesAt);

        if (opensDate >= closesDate) {
          errors.closesAt = "Closing time must be after opening time";
        }

        const now = new Date();
        if (opensDate < now) {
          errors.opensAt = "Opening time cannot be in the past";
        }
      }
    }

    return errors;
  };

  // Add assessment handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!permissions.classroom_assessment_create) {
      toast.warning("You don't have permission to create assessments");
      return;
    }

    if (!classroomId) {
      toast.error("Classroom ID is missing. Please navigate from the classroom dashboard.");
      return;
    }

    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      let payload;

      if (formData.isTimeBased) {
        // Convert dates to UTC based on selected timezone
        const opensAtLocal = moment.tz(formData.opensAt, formData.timeZone);
        const closesAtLocal = moment.tz(formData.closesAt, formData.timeZone);

        const formattedOpenDate = opensAtLocal.utc().format();
        const formattedCloseDate = closesAtLocal.utc().format();


        payload = {
          class_id: classroomId,
          assessment_name: formData.assessmentName,
          earning_points: formData.points || 0,
          activate_time: formattedOpenDate,
          deactivate_time: formattedCloseDate,
          duration: Number(formData.duration),
          is_time_based: 1,
          is_active: 1, // Always set to 1 for time-based assessments
          pass_percentage: Number(formData.passingPercentage),
          is_visible: formData.visibility === 'visible' ? 1 : 0,
          time_zone: formData.timeZone // Add timezone to payload
        };
      } else {
        payload = {
          class_id: classroomId,
          assessment_name: formData.assessmentName,
          earning_points: formData.points || 0,
          activate_time: "",
          deactivate_time: "",
          duration: Number(formData.duration),
          is_time_based: 0,
          is_active: formData.status === 'Active' ? 1 : 0,
          pass_percentage: Number(formData.passingPercentage),
          is_visible: formData.visibility === 'visible' ? 1 : 0,
          time_zone: formData.timeZone // Add timezone to payload
        };
      }

      const response = await addAssessmentToClassroom(payload);

      if (response.success) {
        toast.success(response.message || `Assessment "${formData.assessmentName}" has been created`);
        handleModalClose();
        fetchAssessments(pagination.page, searchTerm);
      } else {
        toast.error(response.message || "Failed to create assessment");
      }
    } catch (error) {
      console.error("Error creating assessment:", error);
      toast.error("An error occurred while creating the assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit assessment handler
  const handleEditAssessment = (assessment) => {
    if (!permissions.classroom_assessment_edit) {
      toast.warning("You don't have permission to edit assessments");
      return;
    }

    // Convert UTC dates to local time in the selected timezone
    const timezone = assessment.raw_time_zone || DefaultTimeZone;
    const opensAt = assessment.activate_time ? moment(assessment.activate_time).format('YYYY-MM-DDTHH:mm') : '';
    const closesAt = assessment.deactivate_time ? moment(assessment.deactivate_time).format('YYYY-MM-DDTHH:mm') : '';

    setEditFormData({
      assessmentId: assessment.id,
      assessmentName: assessment.title,
      points: assessment.earning_points !== undefined && assessment.earning_points !== null ? assessment.earning_points.toString() : '0',
      isTimeBased: assessment.isTimeBased,
      status: assessment.status,
      duration: assessment.raw_duration.toString(),
      passingPercentage: assessment.pass_percentage.toString(),
      opensAt: opensAt,
      closesAt: closesAt,
      visibility: assessment.is_visible === 1 ? 'visible' : 'hidden',
      timeZone: timezone
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Update assessment handler
  const handleEditSubmit = async () => {
    if (!classroomId || !editFormData.assessmentId) {
      toast.error("Missing required information. Cannot update assessment.");
      return;
    }

    const errors = validateForm(editFormData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsEditing(true);

    try {
      let payload;

      if (editFormData.isTimeBased) {
        // Convert local dates to UTC for API
        const opensAtUTC = moment(editFormData.opensAt).utc().format();
        const closesAtUTC = moment(editFormData.closesAt).utc().format();

        payload = {
          assessment_id: editFormData.assessmentId,
          class_id: classroomId,
          assessment_name: editFormData.assessmentName,
          earning_points: editFormData.points || 0,
          activate_time: opensAtUTC,
          deactivate_time: closesAtUTC,
          duration: Number(editFormData.duration),
          is_time_based: 1,
          is_active: 1, // Always set to 1 for time-based assessments
          pass_percentage: Number(editFormData.passingPercentage),
          is_visible: editFormData.visibility === 'visible' ? 1 : 0,
          time_zone: editFormData.timeZone
        };
      } else {
        payload = {
          assessment_id: editFormData.assessmentId,
          class_id: classroomId,
          assessment_name: editFormData.assessmentName,
          earning_points: editFormData.points || 0,
          activate_time: "",
          deactivate_time: "",
          duration: Number(editFormData.duration),
          is_time_based: 0,
          is_active: editFormData.status === 'Active' ? 1 : 0,
          pass_percentage: Number(editFormData.passingPercentage),
          is_visible: editFormData.visibility === 'visible' ? 1 : 0,
          time_zone: editFormData.timeZone
        };
      }

      const response = await updateAssessmentInClassroom(payload);

      if (response.success) {
        toast.success(response.message || `Assessment "${editFormData.assessmentName}" has been updated`);
        handleEditModalClose();
        fetchAssessments(pagination.page, searchTerm);
      } else {
        toast.error(response.message || "Failed to update assessment");
      }
    } catch (error) {
      console.error("Error updating assessment:", error);
      toast.error("An error occurred while updating the assessment");
    } finally {
      setIsEditing(false);
    }
  };

  // Delete assessment handlers
  const handleDeleteAssessment = (assessment) => {
    if (!permissions.classroom_assessment_delete) {
      toast.warning("You don't have permission to delete assessments");
      return;
    }
    setSelectedAssessment(assessment);
    setShowDeleteModal(true);
  };

  const confirmDeleteAssessment = async () => {
    if (!selectedAssessment) return;

    setIsDeleting(true);

    try {
      const payload = {
        class_id: classroomId,
        assessment_id: selectedAssessment.id
      };

      const response = await removeAssessmentFromClassroom(payload);

      if (response.success) {
        toast.success(response.message || `Assessment "${selectedAssessment.title}" has been deleted`);
        handleDeleteModalClose();
        fetchAssessments(pagination.page, searchTerm);
      } else {
        toast.error(response.message || "Failed to delete assessment");
      }
    } catch (error) {
      console.error("Error deleting assessment:", error);
      toast.error("An error occurred while deleting the assessment");
    } finally {
      setIsDeleting(false);
    }
  };

  // Copy assessment handlers
  const handleCopyAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setShowCopyModal(true);
  };

  const toggleClassroomSelection = (classroomId) => {
    if (selectedClassrooms.includes(classroomId)) {
      setSelectedClassrooms(selectedClassrooms.filter(id => id !== classroomId));
    } else {
      setSelectedClassrooms([...selectedClassrooms, classroomId]);
    }
  };

  const handleCopySubmit = async () => {
    if (selectedClassrooms.length === 0) {
      toast.warn("Please select at least one classroom to copy the assessment to.");
      return;
    }
    if (!selectedAssessment || !classroomId) {
      toast.error("Missing assessment or current classroom information.");
      return;
    }

    setIsCopying(true);
    const payload = {
      current_classroom_id: classroomId,
      copy_classroom_ids: selectedClassrooms,
      assessment_id: selectedAssessment.id
    };

    // console.log('copy assessment payload', payload)

    try {
      const response = await copyAssessment(payload);
      if (response.success) {
        toast.success(response.message || "Assessment copied successfully to selected classrooms!");
        handleCopyModalClose();
      } else {
        toast.error(response.message || "Failed to copy assessment.");
      }
    } catch (error) {
      console.error("Error copying assessment:", error);
      toast.error("An error occurred while copying the assessment.");
    } finally {
      setIsCopying(false);
    }
  };

  // Filter classrooms for copy modal
  const filteredClassrooms = availableClassrooms.filter(classroom =>
    classroom.name.toLowerCase().includes(classroomSearchTerm.toLowerCase())
  );

  const totalRecords = pagination.totalRecords ?? 0;
  const totalPagesVal = pagination.totalPages ?? 1;
  const pageVal = pagination.page ?? 1;
  const limitVal = pagination.limit ?? 10;
  const startRecord = totalRecords === 0 ? 0 : (pageVal - 1) * limitVal + 1;
  const endRecord = Math.min(pageVal * limitVal, totalRecords);

  return (
    <>
      <div className="overflow-hidden">
        <div className="flex flex-nowrap items-center gap-3 px-0 py-0 border-b-0 bg-transparent overflow-x-auto whitespace-nowrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF]"
              placeholder="Search assessments..."
              value={searchTerm}
              onChange={handleSearch}
              aria-label="Search assessments"
            />
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            onClick={() => setShowModal(true)}
            disabled={!permissions.classroom_assessment_create}
            title={!permissions.classroom_assessment_create ? "You don't have permission to create assessments" : "Add Assessment"}
          >
            <Icon icon="fluent:add-24-regular" width="18" height="18" className="shrink-0" />
            <span>Add Assessment</span>
          </button>
        </div>

        <div className="p-0 mt-4">
      <div className="flex flex-col">
        {loading ? (
          <div className="w-full flex justify-center my-5">
            <Loader />
          </div>
        ) : assessments.length === 0 ? (
          <div className="w-full flex justify-center my-5">
            <NoData message="No assessments found" />
          </div>
        ) : (
          assessments.map(assessment => (
            <div className="w-full mb-3" key={assessment.id}>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="p-4 sm:p-5">
                  {/* title and actions */}
                  <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h5 className="mb-0 text-base font-semibold text-gray-900 truncate">
                      {assessment.title}
                    </h5>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                          assessment.is_visible === 1
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}
                      >
                        {assessment.is_visible === 1 ? 'Visible to Trainees' : 'Hidden from Trainees'}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          assessment.is_active === 1
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            assessment.is_active === 1 ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
                          }`}
                        />
                        {assessment.is_active === 1 ? 'Assessment Ongoing' : 'Assessment Ended'}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-primary text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!permissions.classroom_assessment_edit ? "You don't have permission to edit assessments" : 'Edit'}
                          onClick={() => handleEditAssessment(assessment)}
                          disabled={!permissions.classroom_assessment_edit}
                        >
                          <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-primary text-primary hover:bg-primary/10"
                          title="Copy to Other Classrooms"
                          onClick={() => handleCopyAssessment(assessment)}
                        >
                          <Icon icon="fluent:copy-24-regular" width="18" height="18" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!permissions.classroom_assessment_delete ? "You don't have permission to delete assessments" : 'Delete'}
                          onClick={() => handleDeleteAssessment(assessment)}
                          disabled={!permissions.classroom_assessment_delete}
                        >
                          <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* assessment details */}
                  <div className="w-full mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Icon icon="fluent:timer-24-regular" width="20" height="20" className="text-gray-500" />
                          <span>Duration: {assessment.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Icon icon="fluent:checkmark-circle-24-regular" width="20" height="20" className="text-gray-500" />
                          <span>Passing Percentage: {assessment.passingPercentage}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {assessment.manualActivation ? (
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Icon icon="fluent:calendar-24-regular" width="20" height="20" className="text-gray-500" />
                            <span>Manual activation: No time constraints</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <Icon icon="fluent:calendar-24-regular" width="20" height="20" className="text-gray-500" />
                              <span>
                                Opens:{' '}
                                {assessment.opensAt
                                  ? moment.utc(assessment.opensAt).local().format('DD MMM YYYY, hh:mm A')
                                  : 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <Icon icon="fluent:calendar-24-regular" width="20" height="20" className="text-gray-500" />
                              <span>
                                Closes:{' '}
                                {assessment.closesAt
                                  ? moment.utc(assessment.closesAt).local().format('DD MMM YYYY, hh:mm A')
                                  : 'N/A'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status Message */}
                  <div className="w-full mb-4">
                    {assessment.status === 'Active' ? (
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-green-600">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <span>Trainees can attempt this assessment while it is active.</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                        <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full" />
                        <span>
                          {assessment.activate_time
                            ? `Opens on ${new Date(assessment.activate_time.replace(' ', 'T')).toLocaleDateString(
                                'en-US',
                                { month: 'short', day: '2-digit' }
                              )}`
                            : 'Manual activation required'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* assessment footer */}
                  <div className="w-full pt-3 border-t border-gray-100">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex flex-wrap gap-6">
                        <div className="text-center min-w-[80px]">
                          <div className="text-lg sm:text-2xl font-semibold text-primary leading-tight">
                            {assessment.stats.passingPercentage}
                          </div>
                          <small className="text-gray-500 text-xs sm:text-sm">Passing Percentage</small>
                        </div>
                        <div className="text-center min-w-[80px]">
                          <div className="text-lg sm:text-2xl font-semibold text-primary leading-tight">
                            {assessment.stats.attended}
                          </div>
                          <small className="text-gray-500 text-xs sm:text-sm">Attended</small>
                        </div>
                        <div className="text-center min-w-[80px]">
                          <div className="text-lg sm:text-2xl font-semibold text-primary leading-tight">
                            {assessment.stats.noOfDue}
                          </div>
                          <small className="text-gray-500 text-xs sm:text-sm">No. of Questions</small>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                          onClick={() => HandleAssessmentManage(assessment.id)}
                          disabled={!permissions.classroom_assessment_manage}
                          title={!permissions.classroom_assessment_manage ? "You don't have permission to manage assessments" : "Manage Assessment"}
                        >
                          <Icon icon="fluent:settings-24-regular" width="18" height="18" />
                          <span>Manage</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Assessment Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={handleModalClose}>
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 items-center" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="mb-0">Add Assessment</h5>
              <button
                onClick={handleModalClose}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <Icon icon="fluent:dismiss-24-regular" width="24" height="24" />
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-font-primary mb-1.5">Assessment Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.assessmentName ? 'border-red-500' : 'border-input-border'}`}
                    name="assessmentName"
                    value={formData.assessmentName}
                    onChange={handleInputChange}
                    placeholder="Enter assessment name"
                  />
                  {formErrors.assessmentName && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.assessmentName}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-font-primary mb-1.5">Points</label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.points ? 'border-red-500' : 'border-input-border'}`}
                    name="points"
                    value={formData.points}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                  />
                  <small className="text-gray-500 text-xs">
                    Reward points trainees receive after successfully completing this assessment.
                  </small>
                  {formErrors.points && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.points}</div>
                  )}
                </div>

                <div className="mb-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      id="isTimeBased"
                      name="isTimeBased"
                      checked={formData.isTimeBased}
                      onChange={handleInputChange}
                    />
                    <label className="text-sm font-medium text-font-primary" htmlFor="isTimeBased">
                      Is Time Based
                    </label>
                  </div>
                  <div className="text-gray-500 text-sm mt-1">
                    Time-based assessments will automatically be set to Active when the opening time is reached.
                  </div>
                </div>

                {!formData.isTimeBased && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-font-primary mb-1.5">Status</label>
                    <select
                      className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Inactive">Inactive</option>
                      <option value="Active">Active</option>
                    </select>
                    <div className="text-gray-500 text-sm mt-1">
                      Default is Inactive. Set to Active if you want to make it available immediately.
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="block text-sm font-medium text-font-primary mb-1.5">Visibility</label>
                  <select
                    className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                    name="visibility"
                    value={formData.visibility}
                    onChange={handleInputChange}
                  >
                    <option value="hidden">Hidden from Trainees</option>
                    <option value="visible">Visible to Trainees</option>
                  </select>
                  <div className="text-gray-500 text-sm mt-1">
                    Control whether trainees can see this assessment in their list
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-font-primary mb-1.5">Duration (minutes) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.duration ? 'border-red-500' : 'border-input-border'}`}
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="Enter duration in minutes"
                  />
                  {formErrors.duration && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.duration}</div>
                  )}
                  <div className="text-gray-500 text-sm mt-1">
                    Time allowed for trainees to complete the assessment once started
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-font-primary mb-1.5">Passing Percentage <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.passingPercentage ? 'border-red-500' : 'border-input-border'}`}
                    name="passingPercentage"
                    value={formData.passingPercentage}
                    onChange={handleInputChange}
                    placeholder="Enter passing percentage"
                    min="1"
                    max="100"
                  />
                  {formErrors.passingPercentage && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.passingPercentage}</div>
                  )}
                  <div className="text-gray-500 text-sm mt-1">
                    Percentage required to pass the assessment
                  </div>
                </div>

            {formData.isTimeBased && (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium mb-1">Time Zone <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <div
                      className={`flex items-center justify-between w-full px-3 py-2 border rounded-lg cursor-pointer ${formErrors.timeZone ? 'border-red-500' : 'border-gray-300'}`}
                      onClick={() => setIsTimeZoneDropdownOpen(!isTimeZoneDropdownOpen)}
                      style={{
                        borderRadius: '8px',
                        border: formErrors.timeZone ? '1px solid #dc3545' : '1px solid #e0e0e0',
                        padding: '12px 16px',
                        fontSize: '0.875rem',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      <span>{formData.timeZone ? timeZones.find(zone => zone.value === formData.timeZone)?.label : 'Select Time Zone'}</span>
                      <Icon icon={isTimeZoneDropdownOpen ? "eva:arrow-up-fill" : "eva:arrow-down-fill"} />
                    </div>
                    {isTimeZoneDropdownOpen && (
                      <div
                        className="absolute left-0 right-0 bg-white border border-gray-200 rounded-xl mt-1 shadow-lg"
                        style={{
                          maxHeight: '300px',
                          overflowY: 'auto',
                          zIndex: 1000
                        }}
                      >
                        <div className="p-2 sticky top-0 bg-white">
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                              placeholder="Search timezone..."
                              value={timeZoneSearch}
                              onChange={(e) => setTimeZoneSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                paddingLeft: '35px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                fontSize: '0.875rem'
                              }}
                            />
                            <Icon
                              icon="eva:search-outline"
                              style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#6c757d'
                              }}
                            />
                          </div>
                        </div>
                        <div className="timezone-options">
                          {timeZones
                            .filter(zone =>
                              zone.label.toLowerCase().includes(timeZoneSearch.toLowerCase()) ||
                              zone.value.toLowerCase().includes(timeZoneSearch.toLowerCase())
                            )
                            .map((zone) => (
                              <div
                                key={zone.value}
                                className="timezone-option"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, timeZone: zone.value }));
                                  setIsTimeZoneDropdownOpen(false);
                                  setTimeZoneSearch('');
                                }}
                                style={{
                                  padding: '10px 16px',
                                  cursor: 'pointer',
                                  backgroundColor: formData.timeZone === zone.value ? '#f8f9fa' : 'transparent',
                                  transition: 'background-color 0.2s',
                                  fontSize: '0.875rem',
                                  color: '#333'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData.timeZone === zone.value ? '#f8f9fa' : 'transparent'}
                              >
                                {zone.label}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>
                  {formErrors.timeZone && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.timeZone}</div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Opens At <span className="text-red-500">*</span></label>
                    <input
                      type="datetime-local"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.opensAt ? 'border-red-500' : 'border-gray-300'}`}
                      name="opensAt"
                      value={formData.opensAt}
                      onChange={handleInputChange}
                    />
                    {formErrors.opensAt && (
                      <div className="text-red-500 text-sm mt-1">{formErrors.opensAt}</div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Closes At <span className="text-red-500">*</span></label>
                    <input
                      type="datetime-local"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.closesAt ? 'border-red-500' : 'border-gray-300'}`}
                      name="closesAt"
                      value={formData.closesAt}
                      onChange={handleInputChange}
                    />
                    {formErrors.closesAt && (
                      <div className="text-red-500 text-sm mt-1">{formErrors.closesAt}</div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg disabled:opacity-50"
                onClick={handleModalClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" role="status" aria-hidden="true"></span>
                    Creating...
                  </>
                ) : (
                  'Save Assessment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
      )}

      {/* Edit Assessment Modal */}
      {showEditModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={handleEditModalClose}>
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 items-center" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="mb-0">Edit Assessment</h5>
              <button
                onClick={handleEditModalClose}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <Icon icon="fluent:dismiss-24-regular" width="24" height="24" />
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }}>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-font-primary mb-1.5">Assessment Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.assessmentName ? 'border-red-500' : 'border-input-border'}`}
                    name="assessmentName"
                    value={editFormData.assessmentName}
                    onChange={handleEditInputChange}
                    placeholder="Enter assessment name"
                  />
                  {formErrors.assessmentName && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.assessmentName}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-font-primary mb-1.5">Points</label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.points ? 'border-red-500' : 'border-input-border'}`}
                    name="points"
                    value={editFormData.points}
                    onChange={handleEditInputChange}
                    placeholder="Enter points"
                    min="0"
                  />
                  <small className="text-gray-500">Trainees will get these points once they complete this assessment</small>
                  {formErrors.points && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.points}</div>
                  )}
                </div>

                <div className="mb-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      id="editIsTimeBased"
                      name="isTimeBased"
                      checked={editFormData.isTimeBased}
                      onChange={handleEditInputChange}
                      disabled={true}
                    />
                    <label className="text-sm font-medium text-font-primary" htmlFor="editIsTimeBased">
                      Is Time Based
                    </label>
                  </div>
                  <div className="text-gray-500 text-sm mt-1">
                    Time-based setting cannot be changed after creation.
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-font-primary mb-1.5">Visibility</label>
                  <select
                    className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                    name="visibility"
                    value={editFormData.visibility}
                    onChange={handleEditInputChange}
                  >
                    <option value="hidden">Hidden from Trainees</option>
                    <option value="visible">Visible to Trainees</option>
                  </select>
                  <div className="text-gray-500 text-sm mt-1">
                    Control whether trainees can see this assessment in their list
                  </div>
                </div>

                {editFormData.isTimeBased ? (
                  <>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-font-primary mb-1.5">Duration (minutes) <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.duration ? 'border-red-500' : 'border-input-border'}`}
                        name="duration"
                        value={editFormData.duration}
                        onChange={handleEditInputChange}
                        placeholder="Enter duration in minutes"
                      />
                      {formErrors.duration && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.duration}</div>
                      )}
                      <div className="text-gray-500 text-sm mt-1">
                        Time allowed for trainees to complete the assessment once started
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-font-primary mb-1.5">Passing Percentage <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.passingPercentage ? 'border-red-500' : 'border-input-border'}`}
                        name="passingPercentage"
                        value={editFormData.passingPercentage}
                        onChange={handleEditInputChange}
                        placeholder="Enter passing percentage"
                        min="1"
                        max="100"
                      />
                      {formErrors.passingPercentage && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.passingPercentage}</div>
                      )}
                      <div className="text-gray-500 text-sm mt-1">
                        Percentage required to pass the assessment
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-font-primary mb-1.5">Time Zone <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div
                          className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between cursor-pointer ${formErrors.timeZone ? 'border-red-500' : 'border-input-border'}`}
                          onClick={() => setIsTimeZoneDropdownOpen(!isTimeZoneDropdownOpen)}
                        >
                          <span>{editFormData.timeZone ? timeZones.find(zone => zone.value === editFormData.timeZone)?.label : 'Select Time Zone'}</span>
                          <Icon icon={isTimeZoneDropdownOpen ? "eva:arrow-up-fill" : "eva:arrow-down-fill"} />
                        </div>
                        {isTimeZoneDropdownOpen && (
                          <div
                            className="absolute w-full bg-white border border-gray-300 rounded-xl mt-1 shadow-sm"
                            style={{
                              maxHeight: '300px',
                              overflowY: 'auto',
                              zIndex: 1000
                            }}
                          >
                            <div className="p-2 sticky top-0 bg-white">
                              <div className="relative">
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                  placeholder="Search timezone..."
                                  value={timeZoneSearch}
                                  onChange={(e) => setTimeZoneSearch(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <Icon
                                  icon="eva:search-outline"
                                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                                />
                              </div>
                            </div>
                            <div>
                              {timeZones
                                .filter(zone =>
                                  zone.label.toLowerCase().includes(timeZoneSearch.toLowerCase()) ||
                                  zone.value.toLowerCase().includes(timeZoneSearch.toLowerCase())
                                )
                                .map((zone) => (
                                  <div
                                    key={zone.value}
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${editFormData.timeZone === zone.value ? 'bg-gray-100' : ''}`}
                                    onClick={() => {
                                      setEditFormData(prev => ({ ...prev, timeZone: zone.value }));
                                      setIsTimeZoneDropdownOpen(false);
                                      setTimeZoneSearch('');
                                    }}
                                  >
                                    {zone.label}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>
                      {formErrors.timeZone && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.timeZone}</div>
                      )}
                    </div>

                    <div className="flex gap-4">
                      <div className="md:w-1/2 mb-3">
                        <label className="block text-sm font-medium text-font-primary mb-1.5">Opens At <span className="text-red-500">*</span></label>
                        <input
                          type="datetime-local"
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.opensAt ? 'border-red-500' : 'border-input-border'}`}
                          name="opensAt"
                          value={
                            editFormData.opensAt
                              ? moment.utc(editFormData.opensAt).tz(editFormData.timeZone).format('YYYY-MM-DDTHH:mm')
                              : ''
                          }
                          onChange={handleEditInputChange}
                          min={moment().tz(editFormData.timeZone).format('YYYY-MM-DDTHH:mm')}
                        />
                        {formErrors.opensAt && (
                          <div className="text-red-500 text-sm mt-1">{formErrors.opensAt}</div>
                        )}
                      </div>

                      <div className="md:w-1/2 mb-3">
                        <label className="block text-sm font-medium text-font-primary mb-1.5">Closes At <span className="text-red-500">*</span></label>
                        <input
                          type="datetime-local"
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.closesAt ? 'border-red-500' : 'border-input-border'}`}
                          name="closesAt"
                          value={
                            editFormData.closesAt
                              ? moment.utc(editFormData.closesAt).tz(editFormData.timeZone).format('YYYY-MM-DDTHH:mm')
                              : ''
                          }
                          onChange={handleEditInputChange}
                          min={moment().tz(editFormData.timeZone).format('YYYY-MM-DDTHH:mm')}
                        />
                        {formErrors.closesAt && (
                          <div className="text-red-500 text-sm mt-1">{formErrors.closesAt}</div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-font-primary mb-1.5">Status</label>
                      <select
                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                        name="status"
                        value={editFormData.status}
                        onChange={handleEditInputChange}
                      >
                        <option value="Inactive">Inactive</option>
                        <option value="Active">Active</option>
                      </select>
                      <div className="text-gray-500 text-sm mt-1">
                        Set to Active to make the assessment available to trainees.
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-font-primary mb-1.5">Duration (minutes) <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.duration ? 'border-red-500' : 'border-input-border'}`}
                        name="duration"
                        value={editFormData.duration}
                        onChange={handleEditInputChange}
                        placeholder="Enter duration in minutes"
                      />
                      {formErrors.duration && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.duration}</div>
                      )}
                      <div className="text-gray-500 text-sm mt-1">
                        Time allowed for trainees to complete the assessment once started
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-font-primary mb-1.5">Passing Percentage <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.passingPercentage ? 'border-red-500' : 'border-input-border'}`}
                        name="passingPercentage"
                        value={editFormData.passingPercentage}
                        onChange={handleEditInputChange}
                        placeholder="Enter passing percentage"
                        min="1"
                        max="100"
                      />
                      {formErrors.passingPercentage && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.passingPercentage}</div>
                      )}
                      <div className="text-gray-500 text-sm mt-1">
                        Percentage required to pass the assessment
                      </div>
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                    onClick={handleEditModalClose}
                    disabled={isEditing}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
                    disabled={isEditing}
                  >
                    {isEditing ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      'Update Assessment'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={handleDeleteModalClose}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 items-center" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center text-red-500">
                <Icon icon="fluent:warning-24-regular" className="mr-2" />
                <h5 className="mb-0">Confirm Deletion</h5>
              </div>
              <button
                onClick={handleDeleteModalClose}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <Icon icon="fluent:dismiss-24-regular" width="24" height="24" />
              </button>
            </div>
            <div className="p-6">
              {selectedAssessment && (
                <div>
                  <p>Are you sure you want to delete the assessment <strong>"{selectedAssessment.title}"</strong>?</p>
                  <p className="text-red-500 mb-0">This action cannot be undone.</p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                onClick={handleDeleteModalClose}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg"
                onClick={confirmDeleteAssessment}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Assessment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Assessment Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={handleCopyModalClose}>
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 items-center" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center">
                <Icon icon="fluent:copy-24-regular" className="mr-2" />
                <h5 className="mb-0">Copy Assessment to Other Classrooms</h5>
              </div>
              <button
                onClick={handleCopyModalClose}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <Icon icon="fluent:dismiss-24-regular" width="24" height="24" />
              </button>
            </div>
            <div className="p-6">
              {selectedAssessment && (
                <div className="mb-3">
                  <h6>Assessment: <span className="text-primary">{selectedAssessment.title}</span></h6>
                  <p className="text-gray-500">Select the classrooms where you want to copy this assessment.</p>
                </div>
              )}

              <div className="mb-3">
                <label className="block text-sm font-medium text-font-primary mb-1.5">Search Classrooms</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Search Live Sessions"
                  value={classroomSearchTerm}
                  onChange={(e) => setClassroomSearchTerm(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-font-primary mb-1.5">Available Classrooms</label>
                <div className="border border-gray-300 rounded-lg p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {filteredClassrooms.length === 0 ? (
                    <div className="text-center text-gray-500 py-3">
                      <Icon icon="fluent:search-24-regular" width="48" height="48" className="mb-2" />
                      <p>No classrooms found</p>
                    </div>
                  ) : (
                    filteredClassrooms.map(classroom => (
                      <div key={classroom.id} className="flex items-center mb-2">
                        <input
                          className="mr-2"
                          type="checkbox"
                          id={`classroom-${classroom.id}`}
                          checked={selectedClassrooms.includes(classroom.id)}
                          onChange={() => toggleClassroomSelection(classroom.id)}
                        />
                        <label className="text-sm font-medium text-font-primary" htmlFor={`classroom-${classroom.id}`}>
                          {classroom.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {selectedClassrooms.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
                  <Icon icon="fluent:info-24-regular" className="mr-2" />
                  Selected {selectedClassrooms.length} classroom{selectedClassrooms.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                onClick={handleCopyModalClose}
                disabled={isCopying}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
                onClick={handleCopySubmit}
                disabled={isCopying || selectedClassrooms.length === 0}
              >
                {isCopying ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Copying...
                  </>
                ) : (
                  <>
                    <Icon icon="fluent:copy-24-regular" className="mr-2" />
                    Copy Assessment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && assessments.length > 0 && totalPagesVal > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span className="text-gray-500">
              {totalRecords === 0 ? 'No records' : `Showing ${startRecord}–${endRecord} of ${totalRecords}`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => fetchAssessments(pageVal - 1, searchTerm)}
              disabled={pageVal === 1 || totalPagesVal === 0}
              title="Previous page"
            >
              <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
            </button>
            <span className="px-2 text-gray-700">
              Page <span className="font-medium">{totalPagesVal === 0 ? 0 : pageVal}</span> of <span className="font-medium">{totalPagesVal || 0}</span>
            </span>
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => fetchAssessments(pageVal + 1, searchTerm)}
              disabled={pageVal === totalPagesVal || totalPagesVal === 0}
              title="Next page"
            >
              <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
            </button>
          </div>
        </div>
      )}
        </div>
      </div>
    </>
  )
}

export default ClassroomAssessment
